import { asyncFlow, flow, ifThenElse, tap } from '@helo/utils'

import { DownloadLink } from '../../../components/Toast/DownloadLink'
import { showToast } from '../../../components/Toast/showToast'
import {
  createDownloadDocument,
  fetchNotifications,
} from '../../../services/NotificationService'
import { exportFile } from '../../../services/queryService'
import {
  ColumnState,
  TableColumnConfig,
} from '../../../widgets/TableWidget/tablewidget.parts'
import { useAppStore } from '../appStore'
import { NotificationsSlice } from './notificationSlice'
import { NotificationSliceParts } from './notificationSlice.parts'

const getNotifications = () =>
  asyncFlow(
    asyncFlow.inputExtends<NotificationsSlice>(),
    async store => {
      const notifications = await fetchNotifications()
      if (!notifications) return store

      return NotificationSliceParts.setNotifications(notifications)(store)
    },
    showToasts(),
    asyncFlow.catch((error, previous) => {
      console.error('Error fetching notifications:', error)
      return previous
    }),
  )

const showToasts = () =>
  flow(flow.inputExtends<NotificationsSlice>(), store => {
    const localStorageKey = 'heloNotificationsSeen'
    const notifications = store.notifications.notifications

    const localStorageNotifications = localStorage.getItem(localStorageKey)
    const notificationsSeen = localStorageNotifications
      ? JSON.parse(localStorageNotifications)
      : []

    const newUnseenNotifications = notifications.filter(notification => {
      if (notificationsSeen.includes(notification.id)) return false
      if (notification.queryExport?.status !== 'SUCCESS') return false
      if (notification.isRead) return false
      if (
        new Date(notification.createdDate) <
        new Date(Date.now() - 1000 * 60 * 15) // 15 minutes
      )
        return false

      return true
    })

    const newUnseenNotificationIds = newUnseenNotifications.map(
      notification => notification.id,
    )

    localStorage.setItem(
      localStorageKey,
      JSON.stringify([...notificationsSeen, ...newUnseenNotificationIds]),
    )

    setTimeout(() => {
      newUnseenNotifications.forEach(notification => {
        if (
          !notification.queryExport?.fileId ||
          !notification.queryExport?.fileName
        ) {
          return
        }

        showToast({
          id: notification.id,
          title: notification.message,
          children: DownloadLink({
            children: notification.queryExport.fileName,
            onClick: () =>
              useAppStore
                .getState()
                .notifications.downloadHandler(
                  notification.queryExport!.fileId!,
                  notification.queryExport!.fileName!,
                ),
          }),
        })
      })
    }, 1)

    return store
  })

const downloadHandler = (fileId: string, fileName: string) =>
  flow(
    flow.inputExtends<NotificationsSlice>(),
    tap(() => {
      createDownloadDocument(fileId, fileName)
    }),
    startPolling('fast'),
  )

const handlePollingInterval = () =>
  asyncFlow(
    asyncFlow.inputExtends<NotificationsSlice>(),
    ifThenElse(
      NotificationSliceParts.shouldShouldFastPoll(),
      startPolling('fast'),
      startPolling('slow'),
    ),
  )

const startPolling = (speed: 'fast' | 'slow' = 'slow') =>
  asyncFlow(
    asyncFlow.inputExtends<NotificationsSlice>(),
    NotificationSliceActions.getNotifications(),
    NotificationSliceParts.incrementPollingCount(),
    store => store,
    <T extends NotificationsSlice>(store: T): T => ({
      ...store,
      notifications: {
        ...store.notifications,
        pollingCount:
          speed !== store.notifications.pollingSpeed
            ? 0
            : store.notifications.pollingCount,
        pollingSpeed: speed,
      },
    }),
    NotificationSliceParts.startTimeout<NotificationsSlice>(
      () => useAppStore.getState().notifications.handlePollingInterval(),
      store => (store.notifications.pollingSpeed === 'fast' ? 5000 : 30000),
    ),
  )

const requestQueryExport = ({
  queryDefinitionId,
  fileName,
  variables,
  columns,
  columnConfigs,
  connectionLocator,
}: {
  queryDefinitionId?: string
  fileName?: string
  variables?: Record<string, any>
  columns?: ColumnState[]
  columnConfigs?: TableColumnConfig[]
  connectionLocator?: any
}) =>
  asyncFlow(
    asyncFlow.inputExtends<NotificationsSlice>(),
    tap(async store => {
      await exportFile({
        queryDefinitionId: queryDefinitionId!,
        fileName: fileName === '' ? 'default' : fileName!,
        variables: variables || {},
        columns: columns || [],
        columnConfigs: columnConfigs || [],
        connectionLocator: connectionLocator || {},
      })
    }),
    startPolling('fast'),
    asyncFlow.catch((error, previous) => {
      console.error('Error exporting file:', error)

      return previous
    }),
  )

export const NotificationSliceActions = {
  getNotifications,
  showToasts,
  downloadHandler,
  handlePollingInterval,
  startPolling,
  requestQueryExport,
}
